import { useTranslate } from '@tolgee/react';
import { Button, Form, message, Radio, Space } from 'antd';
import type { FormInstance, ButtonProps } from 'antd';
import { useCallback, useState } from 'react';
import { JustRightHeightModal } from 'components/styled/scoped';
import {
  refuseReason,
  refuse as refuseApi,
  RefuseReasonResponse,
  RefuseBody,
} from 'services/api/return';
import { handleBuyerCancellation, queryReturnListV2 } from 'services/api/refund';
import { useRequest } from 'ahooks';
import { RefuseUploadImage, useRefuseUploadImageValidate } from './RefuseUploadImage';
import { UploadFile } from 'antd/es/upload/interface';
import { getApiErrorCodeMsg } from 'services/api/utils/errorCode';
import { DescriptionField } from './DisputeModal';
import { FakeRefuseButton } from './FakeRefuse';
import wait from 'utils/wait';
import toLowerCase from 'utils/toLowerCase';
import { i18nExist } from 'services/i18n/utils';
import posthog from '@leyan/lytics';

const missingReasonMap: Record<string, boolean> = {};

function useGetReasonLabel() {
  const { t } = useTranslate();

  const getReasonLabel = useCallback(
    (reason: string) => {
      const key = `tiktokRefuseReason.${reason}`;

      if (!i18nExist(key) && !missingReasonMap[reason]) {
        missingReasonMap[reason] = true;
        posthog.capture('$refuse_reason_not_found', {
          reason,
        });
      }

      return t({
        key,
        defaultValue: reason,
      });
    },
    [t],
  );

  return {
    t,
    getReasonLabel,
  };
}

type ImplementProps = {
  region?: General.UpperCaseRegion;
  internalStoreId: string;
  form: FormInstance;
  reasons: RefuseReasonResponse['availableReasonList'];
};

function Implement(props: ImplementProps) {
  const { t, getReasonLabel } = useGetReasonLabel();

  const { region, form, reasons, internalStoreId } = props;

  const refuseUploadImageValidate = useRefuseUploadImageValidate(true);

  const [selectedReason, setSelectedReason] = useState<(typeof reasons)[number]>();
  const imageRequired = selectedReason?.requiredEvidenceTypes?.includes('IMAGE_OR_VIDEO');
  const textRequired = selectedReason?.requiredEvidenceTypes?.includes('TEXT');

  return (
    <div>
      <Form
        layout="vertical"
        form={form}
        onValuesChange={(changedValues, values) => {
          const { reasonId } = changedValues || {};
          if (reasonId) {
            const target = reasons.find((_) => _.reasonId === reasonId);
            setSelectedReason(target);
          }
        }}
      >
        <Form.Item required label={t('ViewRefund.tiktok.chooseReason')} name="reasonId">
          <Radio.Group>
            <Space direction="vertical">
              {reasons.map((item) => (
                <Radio key={item.reasonId} value={item.reasonId}>
                  {getReasonLabel(item.text)}
                </Radio>
              ))}
            </Space>
          </Radio.Group>
        </Form.Item>

        <Form.Item
          required={imageRequired}
          name="images"
          label={`${t('trade.上传证据')}${
            imageRequired ? '' : ` (${t('ViewRefund.tiktok.partial')})`
          }`}
          rules={[
            {
              validator: (_, value) => {
                const errorMsg = refuseUploadImageValidate(value);
                return errorMsg ? Promise.reject(errorMsg) : Promise.resolve();
              },
              validateTrigger: ['onSubmit'],
            },
            ...(imageRequired
              ? [
                  {
                    required: true,
                    message: t('trade.上传证据'),
                  },
                ]
              : []),
          ]}
        >
          <RefuseUploadImage
            internalStoreId={internalStoreId}
            reqSource="REFUND_TIKTOK"
            accepts={['jpg', 'jpeg', 'png']}
            countLimit={6}
            sizeLimit={5}
          />
        </Form.Item>

        <Form.Item
          key={String(textRequired)}
          required={textRequired}
          name="comment"
          label={`${t('ViewRefund.tiktok.addition.comment')}${
            textRequired ? '' : ` (${t('ViewRefund.tiktok.partial')})`
          }`}
          rules={[
            ...(textRequired
              ? [
                  {
                    required: true,
                    message: t('department.请输入x', {
                      x: t('ViewRefund.tiktok.addition.comment'),
                    }),
                  },
                ]
              : []),
          ]}
        >
          <DescriptionField
            region={region}
            form={form}
            placeholder={t('trade.请输入')}
            fieldName="comment"
          />
        </Form.Item>
      </Form>
    </div>
  );
}

type TikTokRefuseProps = {
  platform: 'TIKTOK' | 'TIKTOK_US';
  region?: General.UpperCaseRegion;
  returnSn?: string;
  externalStoreId: string;
  internalStoreId: string;
  externalTradeId?: string;
  btnProps?: ButtonProps;
  onRefused?: () => void;
  // 退货退款 和 取消订单 状态的 确认和取消逻辑调用的是不同的接口
  inCanceled?: boolean;
  operate: RefuseBody['operate'];
  dueDateMs?: string;
};

const selectOtherMsg = 'selectOtherMsg';

function TikTokRefuse(props: TikTokRefuseProps) {
  const {
    platform,
    region,
    btnProps,
    externalStoreId,
    returnSn,
    onRefused,
    externalTradeId,
    internalStoreId,
    inCanceled = false,
    operate,
    dueDateMs,
  } = props;
  const { t } = useTranslate();
  const [visible, setVisible] = useState(false);
  const [form] = Form.useForm();
  const { loading, runAsync } = useRequest((params) => refuseReason(params), {
    manual: true,
  });
  const [reasons, setReasons] = useState<RefuseReasonResponse['availableReasonList']>([]);

  const open = async () => {
    try {
      const data = await runAsync({
        externalType: platform,
        externalStoreId,
        operate: operate === 'REFUSE' || operate === 'RETURN_REVIEW_FAIL' ? operate : undefined,
        ...(returnSn ? { returnSn } : { externalTradeId }),
      });

      const { availableReasonList } = data;
      setReasons(availableReasonList || []);
      setVisible(true);
    } catch (e) {
      message.error(t('ViewRefund.获取拒绝原因列表失败，请稍后再试'));
    }
  };

  const [refuseFailed, setRefuseFailed] = useState(false);

  const [submitLoading, setSubmitLoading] = useState(false);

  const submit = () => {
    form.validateFields().then(async (formValues) => {
      const { images, ...values } = formValues;
      if (values.reasonId === undefined) {
        return message.error(t('ViewRefund.tiktok.chooseReason.please'));
      }
      const image = images?.map((item: UploadFile<any>) => {
        return item.response?.data?.platformUrl || item.response?.data?.url;
      });

      setSubmitLoading(true);

      try {
        if (inCanceled) {
          await handleBuyerCancellation({
            externalStoreId,
            orderId: externalTradeId,
            platform: platform,
            isAccept: false,
            image,
            ...values,
          });
        } else {
          await refuseApi({
            externalStoreId,
            returnSn,
            image,
            ...values,
          }).then(() => {
            const judge = (retryTime = 3): Promise<any> => {
              return queryReturnListV2({
                returnSn,
                page: {
                  pageNo: 1,
                  pageSize: 1,
                },
                platform: toLowerCase(platform),
              }).then((res) => {
                if (res.returnOrderDataList[0]?.operate.includes(operate)) {
                  if (retryTime <= 0) {
                    return Promise.reject(new Error(selectOtherMsg));
                  }
                  return wait(2000).then(() => judge(retryTime - 1));
                }
              });
            };
            return judge();
          });
        }
        message.success(t('ViewRefund.tiktok.refuse.already'));
        if (onRefused) {
          onRefused();
        }
        onCancel();
      } catch (e: any) {
        const { data } = e.response || {};
        const { code } = data || {};
        if (code === 1103) {
          message.error(t('ViewRefund.tiktok.order.status.change'));
        } else if (code === 5020) {
          setRefuseFailed(true);
          onCancel();
          message.error(
            t(
              dueDateMs && dueDateMs !== '0' && Number(dueDateMs) < Date.now()
                ? '当前退单已超过处理截止时间，无法进行拒绝操作'
                : '根据Tiktok政策，该退单申请不支持拒绝，如需拒绝请前往卖家中心联系平台客服',
            ),
          );
        } else if (e.message === selectOtherMsg) {
          message.error(t('操作失败，请选择其他原因'));
        } else {
          message.error(getApiErrorCodeMsg(e.response, 'ViewRefund.tiktok.retry'));
        }
      } finally {
        setSubmitLoading(false);
      }
    });
  };
  const onCancel = () => {
    form.resetFields();
    setVisible(false);
  };

  const modalInstance = visible && (
    <JustRightHeightModal
      visible={visible}
      width={600}
      onCancel={onCancel}
      title={t(
        operate === 'RETURN_REVIEW_FAIL'
          ? '包裹异常拒绝退款'
          : 'ViewRefund.tiktok.refuseRefundApplication',
      )}
      okButtonProps={{
        loading: submitLoading,
      }}
      onOk={submit}
    >
      <Implement region={region} internalStoreId={internalStoreId} form={form} reasons={reasons} />
    </JustRightHeightModal>
  );
  return (
    <>
      {modalInstance}
      {refuseFailed ? (
        <FakeRefuseButton
          title={t(
            dueDateMs && dueDateMs !== '0' && Number(dueDateMs) < Date.now()
              ? '当前退单已超过处理截止时间，无法进行拒绝操作'
              : '根据Tiktok政策，该退单申请不支持拒绝，如需拒绝请前往卖家中心联系平台客服',
          )}
        />
      ) : (
        <Button size="small" {...btnProps} loading={loading} onClick={() => open()}>
          {t(operate === 'RETURN_REVIEW_FAIL' ? '包裹异常拒绝退款' : 'tableColumn.拒绝')}
        </Button>
      )}
    </>
  );
}

export default TikTokRefuse;
